import * as React from "react"
import { DepartmentType } from "@/types/billing"
import { zodResolver } from "@hookform/resolvers/zod"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useForm } from "react-hook-form"
import * as z from "zod"

import { addDepartment } from "@/lib/api/billing"
import { useToast } from "@/hooks/use-toast"
import { Button } from "@/components/ui/button"
import { DropdownMenuSeparator } from "@/components/ui/dropdown-menu"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Header } from "@/components/header"

import { Icons } from "../icons"
import { Card } from "../ui/card"
import { Input } from "../ui/input"

const formSchema = z.object({
  name: z.string().min(1, "Name is required"),
})

export default function DepartmentForm({
  setShowAddDepartmentPage,
}: {
  setShowAddDepartmentPage: React.Dispatch<React.SetStateAction<boolean>>
}) {
  const { toast } = useToast()
  const queryClient = useQueryClient()

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
    },
  })

  const mutation = useMutation({
    mutationFn: async (data: DepartmentType) => {
      await addDepartment(data)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["departmentDetails"] })
      toast({
        description: "New Department added successfully.",
      })
      setShowAddDepartmentPage(false)
    },
    onError: async (error: any) => {
      const response = error.response
      let errorMessage = "Please try again."

      if (response && response.status === 400) {
        const errorData = await response.json() // Read the response body
        console.error("API Error Response:", errorData)
        errorMessage = errorData.message || JSON.stringify(errorData)
      }

      toast({
        title: "Something went wrong!",
        description: errorMessage,
        variant: "destructive",
      })
    },
  })

  function handleSubmit(values: z.infer<typeof formSchema>) {
    console.log("Submitting values:", values)
    mutation.mutate(values as DepartmentType)
  }

  return (
    <div className="px-2">
      <Header
        subroutes={[
          {
            route: "/user-role-management/department",
            label: "Department",
          },
        ]}
      >
        Add Department
      </Header>
      <DropdownMenuSeparator className="mt-4" />
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(handleSubmit)}
          className="grid grid-cols-1 gap-4 lg:container md:max-w-[1000px]"
        >
          <Card className="my-4 gap-4 bg-slate-50 p-6">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Name<span className="text-red-600">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </Card>
          <div className="flex flex-row justify-end gap-2 md:mr-4">
            <Button
              variant="outline"
              type="button"
              onClick={() => {
                setShowAddDepartmentPage(false)
              }}
              className="w-28"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={mutation.isPending}
              className="w-28"
            >
              {mutation.isPending && (
                <Icons.loader className="mr-2 animate-spin" />
              )}
              Add
            </Button>
          </div>
        </form>
      </Form>
    </div>
  )
}
